import {
  Handle, Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box, Flex, Heading, Stack, Tag, Text, Tooltip,
  VStack
} from "@chakra-ui/react";
import { NodeDataType } from '../../contexts/types';
import { stringToFixed } from '../../utils/calculation';
import { resolveDisplayPartNoByKey } from '../../utils/utils';
import { useTranslation } from 'react-i18next';
import { StarIcon } from '@chakra-ui/icons'

/**
 * プロセスノードパラメータ
 */
interface ProcessNodeProps {
  data: NodeDataType;
}

/**
 * 文字数に応じてフォントサイズを自動計算
 * @param text 対象テキスト
 * @returns フォントサイズ
 */
const getAutoFontSize = (text: string | undefined | null): string => {
  const len = text?.length ?? 0;
  if (len <= 10) return "14px";
  if (len <= 16) return "12px";
  return "8px";
};

/**
 * プロセスノード
 * @param data ノードデータ
 */
const ProcessNode: React.FC<ProcessNodeProps> = ({ data }) => {
  const { t } = useTranslation();

  return (
    // 外枠
    <Box
      className="my-node"
      style={{
        // 利用側から幅と高さを指定するため、100%固定
        width: '100%',
        height: '100%',
      }}
      zIndex={10}
      p={2}
    // bgColor={"blue.100"}
    >
      {/* パーツNo / サイクルタイム */}
      <Tooltip placement="top" hasArrow sx={{ whiteSpace: "pre-line" }}
        label={data.process.uniqueProcessCycleTimeInSecondByPartNoList?.map((p) => `${resolveDisplayPartNoByKey(p.partNo, data.partNoList)} - ${stringToFixed(p.cycleTime, 1)}${t('features.InvestmentScreening.Equipment.field.seconds')}`).join("\n")}>
        <Box pr={0}>
          {
            // 最大４つまでで制限中
            data.process.uniqueProcessCycleTimeInSecondByPartNoList?.filter((p, i) => i < 4).map((p, idx) => {
              const color = data.colors[p.partNo].color;
              return (
                <Text key={idx} ml={1} fontSize={10} color={color} whiteSpace="nowrap" style={{ textOverflow: "ellipsis", overflow: 'hidden', }}>
                  {`${resolveDisplayPartNoByKey(p.partNo, data.partNoList)} - ${stringToFixed(p.cycleTime, 1)}${t('features.InvestmentScreening.Equipment.field.seconds')}`}
                </Text>
              );
            })
          }
        </Box>
      </Tooltip>

      {/* 内枠 */}
      <Box
        border="1px solid #ccc"
        borderRadius="md"
        bg="white"
        width="100%"
        height={
          (data.process.uniqueProcessCycleTimeInSecondByPartNoList?.length ?? 0) === 0 ? "100%" :
            (data.process.uniqueProcessCycleTimeInSecondByPartNoList?.length ?? 0) === 1 ? "85%" :
              (data.process.uniqueProcessCycleTimeInSecondByPartNoList?.length ?? 0) === 2 ? "75%" :
                (data.process.uniqueProcessCycleTimeInSecondByPartNoList?.length ?? 0) === 3 ? "60%" :
                  (data.process.uniqueProcessCycleTimeInSecondByPartNoList?.length ?? 0) === 4 ? "45%" : ""
        }
        p={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        zIndex={1000}
        cursor={(data && data.onClick) ? "pointer" : "hand"}
        onClick={() => {
          if (data && data.onClick)
            data.onClick(data.process);
        }}
      >
        {data.equipmentData && data.equipmentData.length > 0 && (() => {
          // 単純工、多能工、テクニシャンの各人員数の合計を算出
          const totals = data.equipmentData.reduce(
            (acc, current) => ({
              unskilledWorker: (acc.unskilledWorker || 0) + (current.unskilledWorker || 0),
              multiSkilledWorker: (acc.multiSkilledWorker || 0) + (current.multiSkilledWorker || 0),
              technician: (acc.technician || 0) + (current.technician || 0),
            }),
            { unskilledWorker: 0, multiSkilledWorker: 0, technician: 0 }
          );

          return (
            <Tooltip placement="top" hasArrow sx={{ whiteSpace: "pre-line" }} bg='rgba(66, 153, 225, 0.9)' borderRadius='5px' label={
              <>
                <h3>{t('features.InvestmentScreening.ReportAndDraft.equipmentTable.personnelTitle')}</h3>
                <Flex>
                  <Text w="100px" textAlign="right">{t('features.InvestmentScreening.Equipment.field.unskilledWorker')}：</Text>
                  <Text w="30px" textAlign="right">{totals.unskilledWorker}</Text>
                </Flex>
                <Flex>
                  <Text w="100px" textAlign="right">{t('features.InvestmentScreening.Equipment.field.multiSkilledWorker')}：</Text>
                  <Text w="30px" textAlign="right">{totals.multiSkilledWorker}</Text>
                </Flex>
                <Flex>
                  <Text w="100px" textAlign="right">{t('features.InvestmentScreening.Equipment.field.technician')}：</Text>
                  <Text w="30px" textAlign="right">{totals.technician}</Text>
                </Flex>
              </>
            }>
              <StarIcon color="red.400" mr={1} />
            </Tooltip>
          )
        })()}
        {/* プロセス名 */}
        <h3 style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: (data.process.uniqueProcessCycleTimeInSecondByPartNoList?.length ?? 0) === 4 ? 2 : 3,
          overflow: "hidden",
        }}>
          {/* Item No */}
          <Text
            isTruncated={true}
            fontSize={getAutoFontSize(data.process.uniqueProcessName)}
            title={data.process.uniqueProcessName || ""}>
            {data.process.uniqueProcessName}
          </Text>
          {/* Item Name 初めに登録されている item Name を見つけたものを表示 */}
          <Text
            isTruncated={true}
            fontSize={getAutoFontSize(data.process.uniqueProcessCycleTimeInSecondByPartNoList?.find(u => u.itemName !== undefined)?.itemName)}
            title={data.process.uniqueProcessCycleTimeInSecondByPartNoList?.find(u => u.itemName !== undefined)?.itemName || ""}>
            {data.process.uniqueProcessCycleTimeInSecondByPartNoList?.find(u => u.itemName !== undefined)?.itemName || ""}
          </Text>
        </h3>
        {/* デバッグ時はプロセスコード表示がよい。 */}
        {/* <h3>{data.process.uniqueProcessId}</h3> */}

        {/* 接続点（右側） */}
        {data.right &&
          <Handle
            type="source"
            position={Position.Right}
            id="right"
            style={{ background: "black" }}
          />}

        {/* 接続点（左側） */}
        {data.left &&
          <Handle
            type="target"
            position={Position.Left}
            id="left"
            style={{ background: "black" }}
          />}
      </Box>
    </Box >
  );
};

export default ProcessNode;
